<script lang="ts">
  import { BALANCE, GAME_MODE, type MatchPhase, type MatchResult } from '@2k48/shared';
  import { audio } from '../../audio/AudioManager';
  import { animateOverlay, animateResult } from '../../game/motion';
  import {
    NetworkBattleController,
    type NetworkCombatUiState,
    type NetworkGameUiState,
  } from '../../game/network/NetworkBattleController';
  import {
    createPrivateMatch,
    isRoomUnavailable,
    joinMatch,
    joinPrivateMatch,
    type MatchSession,
  } from '../../net/client';
  import MatchmakingScreen from '../components/MatchmakingScreen.svelte';
  import BattleCountdown from '../components/BattleCountdown.svelte';
  import PixelButton from '../components/PixelButton.svelte';
  import PixelIcon from '../components/PixelIcon.svelte';
  import PixelLoader from '../components/PixelLoader.svelte';
  import PlayerPanel from '../components/PlayerPanel.svelte';
  import SegmentedBar from '../components/SegmentedBar.svelte';
  import WaveText from '../components/WaveText.svelte';
  import { feedback } from '../../platform/feedback';
  import { isWithinPhaseHold } from '../../net/phases';
  import { i18n } from '../i18n/index.svelte';
  import { router } from '../stores/router.svelte';
  import { session } from '../stores/session.svelte';
  import { announceAchievements, toasts } from '../stores/toasts.svelte';

  const t = $derived(i18n.t);

  let root: HTMLDivElement;
  let host: HTMLDivElement;
  let match: MatchSession | null = null;
  let ui = $state<NetworkGameUiState>({ players: [], localSeat: 0, modeId: '', roomCode: '' });
  let phase = $state<MatchPhase>('lobby');
  let remainingMs = $state(60_000);
  let countdownMs = $state<number>(BALANCE.phases.countdownMs);
  let result = $state<MatchResult | null>(null);
  let combatState = $state<NetworkCombatUiState | null>(null);
  let reconnecting = $state(false);
  let reconnectBy = $state<number | null>(null);
  let rematchWaiting = $state(false);
  let error = $state('');
  /** Which failure the overlay is describing, so its copy matches the toast. */
  let failureKind = $state<'server' | 'room'>('server');
  /**
   * Derived from the server's own phase boundaries, not from a local timer.
   * `startsAt` is when `playing` actually began on the authoritative clock, so
   * a tab that was backgrounded through the flash comes back to the match in
   * progress rather than to a GO! that never got its `setTimeout`.
   */
  let goVisible = $state(false);
  /** Time left in the `lobby` phase, from the server's own matchmaking deadline. */
  let lobbyRemainingMs = $state<number>(BALANCE.phases.matchmakingTimeoutMs);
  /**
   * How long *this player* has been waiting, which is not the same as the room's
   * age: matchmaking drops you into a room that may already have been open for
   * half its timeout, and reporting that as your wait means the counter starts
   * at 00:25. Deliberately local — "how long have I been here" is a fact about
   * this client, not authoritative match state.
   */
  let queueStartedAt = 0;
  let queueElapsedMs = $state(0);
  /**
   * Bumping this re-runs the connection effect, which is what the retry action
   * on the error toast does. Cheaper and far more predictable than reloading
   * the document: the cleanup below already tears the old session down.
   */
  let attempt = $state(0);
  let lastCountdownDigit: number | null = null;
  let announcedResultId = '';

  /** Stable toast keys, so a flapping link coalesces instead of stacking. */
  const CONNECTION_TOAST = 'versus:connection';
  const SERVER_TOAST = 'versus:server';

  const local = $derived(ui.players.find((player) => player.seat === ui.localSeat));
  const opponent = $derived(ui.players.find((player) => player.seat !== ui.localSeat));
  /**
   * Am I facing a CPU?
   *
   * Asked of the room first and the route second, because the answer can change
   * mid-queue: a public match that finds nobody fills the second seat with a
   * bot and rewrites `modeId`. The route only knows what was *asked* for, which
   * before the connection exists is all there is to go on.
   */
  const isBot = $derived(ui.modeId === GAME_MODE.BOT || router.current.params.mode === 'bot');
  /** Which of the four, when the player chose. Empty for the queue fallback. */
  const botDifficulty = $derived(router.current.params.difficulty ?? '');
  const clock = $derived(formatClock(remainingMs));
  /**
   * The public queue gets its own screen; a private room keeps the battle HUD,
   * because the mockup's lobby *is* the HUD with the board idle behind it — the
   * two players are already known to each other and the panels have something
   * to say. A blind queue has neither.
   */
  const isPrivate = $derived(Boolean(ui.roomCode));
  const inQueue = $derived(phase === 'lobby' && !error && !isBot && !isPrivate);
  const lobbyElapsedMs = $derived(
    Math.max(0, BALANCE.phases.matchmakingTimeoutMs - lobbyRemainingMs),
  );
  const localResult = $derived(result?.seats.find((seat) => seat.seat === ui.localSeat));
  const resultTitle = $derived(
    localResult?.outcome === 'win'
      ? i18n.t.battle.victory
      : localResult?.outcome === 'loss'
        ? i18n.t.battle.defeat
        : localResult?.outcome === 'draw'
          ? i18n.t.battle.draw
          : i18n.t.toasts.matchOver,
  );

  $effect(() => {
    const next = result;
    if (phase !== 'ended' || !next || announcedResultId === next.matchId) return;
    const seat = next.seats.find((entry) => entry.seat === ui.localSeat);
    if (!seat) return;
    announcedResultId = next.matchId;
    if (seat.outcome === 'win') {
      audio.playMusic('victory');
      feedback.emit('match.victory', { audio: false });
    } else if (seat.outcome === 'loss') {
      audio.playMusic('defeat');
      feedback.emit('match.defeat', { audio: false });
    } else {
      audio.stopMusic();
    }
  });

  $effect(() => {
    void attempt;
    let cancelled = false;
    const controller = new NetworkBattleController(host, {
      combatRoot: root,
      onState: (state) => {
        const hadOpponent = ui.players.length > 1;
        ui = state;
        // Announced once, on the transition: the state message arrives on every
        // patch, so a bare `players.length > 1` check would fire continuously.
        if (!hadOpponent && state.players.length > 1 && phase === 'lobby') {
          feedback.emit('ui.confirm');
          // A player who queued for a human and got a CPU is told so. The room
          // rewrote `modeId` on the way in; saying "rival encontrado" here
          // would be the one moment this screen could mislead somebody about
          // who they are playing.
          const filledIn = state.modeId === GAME_MODE.BOT && router.current.params.mode !== 'bot';
          if (filledIn) toasts.warning(i18n.t.toasts.botFilledIn);
          else toasts.success(i18n.t.toasts.opponentFound);
        }
      },
      onPhase: (envelope) => {
        phase = envelope.phase;
        reconnecting = false;
        syncPhaseMusic(envelope.phase);
        if (envelope.phase === 'lobby') {
          queueStartedAt = Date.now();
          queueElapsedMs = 0;
        }
        if (envelope.phase === 'countdown') {
          countdownMs = BALANCE.phases.countdownMs;
          lastCountdownDigit = null;
        }
        if (envelope.phase === 'playing') {
          remainingMs = Math.max(0, (envelope.endsAt ?? 0) - envelope.serverTime);
          goVisible = isWithinPhaseHold(envelope, remainingMs, BALANCE.phases.goHoldMs);
          feedback.emit('match.go');
        }
      },
      onPhaseRefresh: (envelope) => {
        const seat = numberFrom(envelope.meta?.reconnectingSeat);
        const by = numberFrom(envelope.meta?.reconnectBy);
        reconnecting = seat !== null;
        reconnectBy = by;
      },
      onProgress: (remaining, envelope) => {
        if (envelope.phase === 'lobby') {
          lobbyRemainingMs = remaining;
          // Driven off the phase tick we already subscribe to, rather than a
          // second timer of its own.
          queueElapsedMs = Date.now() - queueStartedAt;
        }
        if (envelope.phase === 'countdown') {
          countdownMs = remaining;
          announceCountdownBeat(remaining);
        }
        if (envelope.phase === 'playing') {
          remainingMs = remaining;
          // Re-evaluated every frame off the same synced clock that drives the
          // round timer, so the flash ends at one authoritative instant.
          goVisible = isWithinPhaseHold(envelope, remaining, BALANCE.phases.goHoldMs);
        }
      },
      onResult: (next) => {
        result = next;
        announceOutcome(next);
      },
      onAchievements: (ids) => announceAchievements(ids),
      onCombat: (next) => (combatState = next),
      onReset: () => {
        result = null;
        combatState = null;
        announcedResultId = '';
        rematchWaiting = false;
        reconnecting = false;
      },
      onDrop: (by) => {
        reconnecting = true;
        reconnectBy = by;
        toasts.warning(i18n.t.toasts.unstableConnection, { key: CONNECTION_TOAST });
      },
      onReconnect: () => {
        reconnecting = false;
        reconnectBy = null;
        toasts.dismissKey(CONNECTION_TOAST);
        toasts.success(i18n.t.toasts.connectionRestored, { key: CONNECTION_TOAST });
      },
      onError: (reason) => announceFailure(reason),
    });

    const identity = {
      ...(session.profile?.id ? { playerId: session.profile.id } : {}),
      displayName: session.profile?.displayName ?? i18n.t.common.player,
    };
    const queue = router.current.params.queue;
    const connecting =
      queue === 'private-create'
        ? createPrivateMatch(identity, controller.handlers)
        : queue === 'private-join'
          ? joinPrivateMatch(router.current.params.code ?? '', identity, controller.handlers)
          : joinMatch(
              isBot ? GAME_MODE.BOT : GAME_MODE.VERSUS,
              isBot && botDifficulty ? { ...identity, difficulty: botDifficulty } : identity,
              controller.handlers,
            );

    void connecting
      .then((connected) => {
        if (cancelled) {
          void connected.leave();
          return;
        }
        match = connected;
        controller.attach(connected);
        if (queue === 'private-create') toasts.success(i18n.t.toasts.matchCreated);
      })
      .catch((reason) => {
        if (!cancelled) announceFailure(reason);
      });

    return () => {
      cancelled = true;
      toasts.dismissKey(CONNECTION_TOAST);
      const leaving = match;
      match = null;
      controller.destroy();
      if (leaving) void leaving.leave();
    };
  });

  /**
   * The network layer reports one failure; the player has two different
   * problems. A room that is gone is something they fix by typing a different
   * code, so it is a warning with no retry — retrying the same dead code would
   * only fail again. A server that will not answer is worth retrying, so that
   * one carries the action. `error` still drives the full-screen overlay.
   */
  function announceFailure(reason: unknown): void {
    error = messageOf(reason);

    if (isRoomUnavailable(reason)) {
      failureKind = 'room';
      toasts.warning(i18n.t.toasts.codeExpired, { key: SERVER_TOAST });
      return;
    }

    failureKind = 'server';

    toasts.error(i18n.t.toasts.couldNotConnectServer, {
      key: SERVER_TOAST,
      action: {
        label: i18n.t.common.retry,
        run: () => {
          error = '';
          attempt += 1;
        },
      },
    });
  }

  /**
   * A win by forfeit reads as an unexplained instant victory unless we say why.
   * Only the player who stayed is told: the one who left knows.
   */
  function announceOutcome(next: MatchResult): void {
    const seat = next.seats.find((entry) => entry.seat === ui.localSeat);
    if (!seat || seat.outcome === 'loss') return;
    if (next.reason !== 'forfeit' && next.reason !== 'disconnect') return;

    toasts.info(i18n.t.toasts.opponentLeft);
  }

  function leaveBattle(): void {
    if (phase === 'playing') match?.forfeit();
    router.go('play');
  }

  function requestRematch(): void {
    match?.rematch();
    rematchWaiting = true;
  }

  function announceCountdownBeat(remaining: number): void {
    if (remaining > BALANCE.phases.countdownMs - BALANCE.phases.countdownReadyHoldMs) return;
    const digit = Math.min(3, Math.max(1, Math.ceil(remaining / 1000)));
    if (digit === lastCountdownDigit) return;
    lastCountdownDigit = digit;
    feedback.emit('match.countdown');
  }

  function syncPhaseMusic(nextPhase: MatchPhase): void {
    if (nextPhase === 'lobby' || nextPhase === 'countdown') audio.playMusic('match');
    else if (nextPhase === 'playing') audio.playMusic('battle');
    else if (nextPhase === 'ko') audio.stopMusic();
  }

  async function shareCode(): Promise<void> {
    if (!ui.roomCode) return;
    const text = `2K48 BATTLE · CODIGO ${ui.roomCode}`;
    try {
      if (navigator.share) await navigator.share({ text });
      else await navigator.clipboard.writeText(ui.roomCode);
    } catch {
      // Cancelling the native share sheet is not an error state.
    }
  }

  function formatClock(ms: number): string {
    const seconds = Math.max(0, Math.ceil(ms / 1000));
    return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  }

  function numberFrom(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  function messageOf(reason: unknown): string {
    return reason instanceof Error ? reason.message : String(reason);
  }
</script>

<div class="view versus battle-view" bind:this={root}>
  {#if inQueue}
    <header class="view__header">
      <button class="topbar__back" onclick={leaveBattle} aria-label="Volver">
        <PixelIcon name="back" size={28} />
      </button>
      <h1 class="view__title">BUSCANDO RIVAL</h1>
      <span class="topbar__spacer" aria-hidden="true"></span>
    </header>

    <div class="view__body">
      <MatchmakingScreen
        name={session.profile?.displayName ?? i18n.t.common.player}
        avatarId={session.profile?.avatarId ?? 0}
        rating={session.rating}
        elapsedMs={queueElapsedMs}
        deadlineElapsedMs={lobbyElapsedMs}
        timeoutMs={BALANCE.phases.matchmakingTimeoutMs}
        oncancel={leaveBattle}
      />
    </div>
  {/if}

  <!--
    The battle tree stays mounted while queueing, just hidden: `host` is the
    imperative board's container and the controller binds to it on mount. Tearing
    it down for the queue and rebuilding it on match found would mean rebuilding
    the renderer at the exact moment the countdown starts.
  -->
  <div class="versus__battle" class:versus__battle--hidden={inQueue}>
    <header class="versus__header">
      <button class="topbar__back" onclick={leaveBattle} aria-label="Volver">
        <PixelIcon name="back" size={28} />
      </button>
      <div class="logo versus__logo">2K48</div>
      <div class="versus__clock">
        <span>{isPrivate && phase === 'lobby' ? t.battle.room : t.battle.round}</span>
        <strong>{isPrivate && phase === 'lobby' ? `#${ui.roomCode}` : clock}</strong>
      </div>
    </header>

    <div class="duoJ versus__players">
      <div data-combat-player="p1">
        <PlayerPanel
          name={local?.name ?? session.profile?.displayName ?? i18n.t.common.player}
          avatarId={session.profile?.avatarId ?? 0}
          player={1}
          level={1}
          score={local?.score ?? 0}
          hp={local?.hp ?? 100}
          status={local?.hp === 0
            ? t.battle.statusKo
            : phase === 'countdown'
              ? t.battle.ready
              : phase === 'lobby'
                ? t.battle.statusReady
                : combatState?.attackerSeat === ui.localSeat
                  ? t.battle.statusAttack
                  : combatState?.targetSeat === ui.localSeat
                    ? t.battle.statusHit
                    : t.battle.statusFight}
          state={combatState?.attackerSeat === ui.localSeat
            ? 'attacking'
            : combatState?.targetSeat === ui.localSeat
              ? 'hurt'
              : 'active'}
          impactDamage={combatState?.targetSeat === ui.localSeat ? combatState.damage : undefined}
        />
      </div>
      <!--
        The `!opponent` guard has to come first on `status`, exactly as it does
        on `state` below. Without it the two seat comparisons resolve to
        `undefined === undefined` while the queue is still empty, and the
        unoccupied panel announces ATTACK instead of waiting.
      -->
      <div data-combat-player="p2">
        <PlayerPanel
          name={opponent?.name ?? '- - - -'}
          avatarId={1}
          player={2}
          level={1}
          score={opponent?.score ?? 0}
          hp={opponent?.hp ?? 100}
          status={!opponent
            ? t.battle.statusWait
            : opponent.hp === 0
              ? t.battle.statusKo
              : phase === 'countdown'
                ? t.battle.ready
                : combatState?.attackerSeat === opponent.seat
                  ? t.battle.statusAttack
                  : combatState?.targetSeat === opponent.seat
                    ? t.battle.statusHit
                    : isBot
                      ? t.battle.statusCpu
                      : t.battle.statusFight}
          state={!opponent
            ? 'empty'
            : combatState?.attackerSeat === opponent.seat
              ? 'attacking'
              : combatState?.targetSeat === opponent.seat
                ? 'hurt'
                : 'active'}
          impactDamage={opponent && combatState?.targetSeat === opponent.seat
            ? combatState?.damage
            : undefined}
        />
      </div>
    </div>

    <div class="cmbJ versus__combo" aria-live="polite">
      {#if phase === 'lobby'}
        <span class="versus__combo-idle">P1 --</span>
        <span class="lbl"
          >◆ {isPrivate ? `${t.battle.room} #${ui.roomCode}` : t.battle.statusLobby} ◆</span
        >
        <span class="versus__combo-idle">-- P2</span>
      {:else if combatState?.powerValue}
        <span>P1 x{local?.combo ?? 0}</span>
        <span class="versus__overload">&#9889; {combatState.powerValue} OVERLOAD &#9889;</span>
        <span>x{opponent?.combo ?? 0} P2</span>
      {:else}
        <span>P1 x{local?.combo ?? 0}</span>
        <span class="lbl">COMBO</span>
        <span>x{opponent?.combo ?? 0} P2</span>
      {/if}
    </div>

    <div
      class="versus__stage"
      class:versus__stage--idle={phase === 'lobby' || phase === 'countdown'}
      class:versus__stage--power={Boolean(combatState?.powerValue)}
      bind:this={host}
    >
      {#if phase === 'lobby' && !error}
        <div class="battle-overlay battle-overlay--stage" use:animateOverlay>
          {#if isPrivate}
            <PixelLoader tone="cyan" />
            <span class="battle-overlay__eyebrow">COMPARTE ESTE CODIGO</span>
            <strong class="battle-overlay__code">{ui.roomCode}</strong>
            <strong class="battle-overlay__waiting">ESPERANDO<br />OPONENTE...</strong>
            <span class="battle-overlay__connected"
              >{Math.min(2, ui.players.length)} / 2 CONECTADOS</span
            >
            <div class="versus__progress">
              <SegmentedBar value={lobbyElapsedMs} max={BALANCE.phases.matchmakingTimeoutMs} />
              <!-- Remaining, not elapsed: this is a real server deadline, and
                 "how long until this gives up" is the actionable half. -->
              <span class="lbl">LA SALA CIERRA EN {formatClock(lobbyRemainingMs)}</span>
            </div>
            <span class="tiny">TOCA INVITAR PARA COMPARTIR</span>
          {:else if isBot}
            <PixelLoader tone="cyan" />
            <strong>PREPARANDO BOT...</strong>
          {:else}
            <PixelLoader tone="mag" />
            <strong>BUSCANDO OPONENTE...</strong>
          {/if}
        </div>
      {:else if phase === 'countdown'}
        <div
          class="battle-overlay battle-overlay--stage battle-overlay--countdown"
          use:animateOverlay
        >
          <BattleCountdown remainingMs={countdownMs} />
        </div>
      {:else if goVisible}
        <div
          class="battle-overlay battle-overlay--stage battle-overlay--clear"
          use:animateOverlay={{ emphasis: true }}
        >
          <div class="battle-overlay__go-row">
            <i class="battle-overlay__spark battle-overlay__spark--cyan"></i>
            <i class="battle-overlay__spark battle-overlay__spark--small"></i>
            <span class="battle-overlay__go">GO!</span>
            <i class="battle-overlay__spark battle-overlay__spark--small"></i>
            <i class="battle-overlay__spark battle-overlay__spark--mag"></i>
          </div>
        </div>
      {:else if phase === 'ko' && result?.reason === 'timeout'}
        <div
          class="battle-overlay battle-overlay--stage battle-overlay--countdown"
          use:animateOverlay={{ emphasis: true }}
        >
          <WaveText
            as="strong"
            class="battle-overlay__go battle-result__title"
            text={t.battle.time}
          />
        </div>
      {/if}
      {#if combatState?.powerValue}
        <strong class="versus__power-label">PODER ACTIVADO</strong>
      {/if}
    </div>

    {#if isPrivate && phase === 'lobby'}
      <!-- The NEXT bar advertises a board that is not accepting input yet; while
         the room waits, the same strip carries the two actions that matter. -->
      <div class="versus__lobby-actions">
        <PixelButton variant="danger" onclick={leaveBattle}>CANCELAR</PixelButton>
        <PixelButton variant="p1" onclick={shareCode}>INVITAR</PixelButton>
      </div>
    {:else}
      <div class="nextbar versus__next" class:versus__next--disabled={phase !== 'playing'}>
        <span class="lbl">NEXT</span>
        <div class="versus__chips" aria-hidden="true"><i></i><i></i></div>
        <span class="versus__swipe">SWIPE <PixelIcon name="arrows" size={24} /></span>
      </div>
    {/if}

    <div class="versus__foot">
      2K48 BATTLE · {isBot ? t.battle.statusBot : t.battle.statusOnline}
    </div>
  </div>

  {#if reconnecting && phase !== 'ended'}
    <div class="battle-overlay battle-overlay--full battle-overlay--connection" use:animateOverlay>
      <PixelIcon name="warning" size={60} />
      <strong>RECONECTANDO...</strong>
      <span>VENTANA DE 15 SEGUNDOS</span>
      {#if reconnectBy}<span class="tiny">HASTA {new Date(reconnectBy).toLocaleTimeString()}</span
        >{/if}
    </div>
  {/if}

  {#if error}
    <div class="battle-overlay battle-overlay--full battle-overlay--interactive" use:animateOverlay>
      <PixelIcon name="warning" size={60} />
      <strong
        >{failureKind === 'room' ? t.toasts.matchUnavailable : t.toasts.couldNotConnect}</strong
      >
      <span class="battle-overlay__message">
        {failureKind === 'room' ? t.toasts.codeExpiredDetail : error}
      </span>
      <PixelButton variant="ghost" onclick={() => router.go('play')}>VOLVER</PixelButton>
    </div>
  {:else if phase === 'ended' && result}
    <div
      class="battle-overlay battle-overlay--full battle-overlay--interactive battle-result"
      use:animateResult
    >
      <span class="battle-overlay__eyebrow">RESULTADO</span>
      <WaveText
        as="strong"
        class="battle-result__title"
        data-outcome={localResult?.outcome}
        text={resultTitle}
      />
      <div class="battle-result__stats">
        <span>PUNTOS <b>{localResult?.score ?? 0}</b></span>
        <span>FICHA MAX <b>{localResult?.stats.maxTile ?? 0}</b></span>
        <span>COMBO MAX <b>x{localResult?.stats.maxCombo ?? 0}</b></span>
      </div>
      <PixelButton variant="p1" disabled={rematchWaiting} onclick={requestRematch}
        >{rematchWaiting ? t.battle.waitingOpponent : t.battle.rematch}</PixelButton
      >
      <PixelButton variant="ghost" onclick={() => router.go('play')}>MENU DE MODOS</PixelButton>
    </div>
  {/if}
</div>
